import { type FC, type ReactNode, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import { DateTimePicker } from "@mui/x-date-pickers";
import { useIsMobile } from "@/shared/hooks";
import CloseIcon from "@mui/icons-material/Close";
import FilterListIcon from "@mui/icons-material/FilterList";
import { useFilter } from "./useFilter";
import InputAdornment from "@mui/material/InputAdornment";
import DeleteIcon from "@mui/icons-material/Delete";
import Box from "@mui/material/Box";
import { ContentsAutocomplete } from "@/partner/features/contents/ui/ContentsAutocomplete";
import { TagsAutocomplete } from "@/partner/features/tags/ui";
import { CreatorsAutocomplete } from "@/partner/features/users/ui/CreatorsAutocomplete";
import { LanguageField } from "@/partner/features/users/ui/LanguageField";
import { PromocodesAutocomplete } from "@/partner/features/promocodes/ui/PromocodesAutocomplete";
import { PromocodeCampaignsAutocomplete } from "@/partner/features/promocodes/ui/PromocodeCampaignsAutocomplete";
import { ProductsAutocomplete } from "@/partner/features/products/ui";
import { LinksAutocomplete } from "@/partner/features/links/ui/LinksAutocomplete";
import { EmailCampaignsAutocomplete } from "@/partner/features/automations/ui/EmailCampaignsAutocomplete";
import { ContentTypeField } from "@/partner/shared/ui/ContentTypeField";
import { CountryCodeField } from "@/partner/shared/ui/CountryCodeField";
import { CurrencyField } from "@/partner/shared/ui/CurrencyField";
import { DeviceOsField } from "@/partner/shared/ui/DeviceOsField";
import { DeviceTypeField } from "@/partner/shared/ui/DeviceTypeField";
import { UtmField } from "@/partner/shared/ui/UtmField";
import { PaymentProviderType } from "@/types/graphql/partner.generated";
import {
  PAYMENT_PROVIDER_LABEL_KEY,
  PAYMENT_PROVIDER_ORDER,
} from "@/partner/shared/utils/paymentProvider";

/**
 * Одиночный селект «своего» поля фильтра (статус, тип, автопродление): общий
 * `fields` знает только про заранее известные поля, а такие наборы у каждого
 * раздела свои. Раньше разделы финансов рисовали их чипами через `subheader`,
 * из-за чего шапка карточки выглядела иначе, чем у Users/Links/Videos.
 */
export interface FilterSelect {
  name: string;
  label: string;
  options: { value: string; label: string }[];
  /**
   * Без пункта «Любой» в начале списка: для селектов, у которых пустого
   * значения не бывает (сортировка в «Хранилище»).
   */
  hideAnyOption?: boolean;
  /** Значение, пока пользователь ничего не выбрал (обычно вместе с hideAnyOption). */
  defaultValue?: string;
}

/** Быстрый фильтр одного поля: чипы со значениями прямо в панели. */
export interface FilterQuickFilter {
  name: string;
  /** Подпись слева от чипов; без неё чипы идут одной строкой без заголовка. */
  label?: string;
  options: { value: string; label: string }[];
}

interface FilterProps {
  title?: string;
  alwaysVisible?: string[];
  subheader?: ReactNode;
  dateChips?: "RELATIVE";
  /**
   * Быстрые фильтры чипами, как в админке: набор значений одного поля
   * прямо в панели, без раскрытия селекта. Повторный клик по выбранному
   * чипу снимает фильтр. Для полей с парой-тройкой значений это короче
   * селекта, для длинных списков остаётся `selects`.
   */
  quickFilters?: FilterQuickFilter[];
  selects?: FilterSelect[];
  fields?: {
    search?: boolean;
    dateFrom?: boolean;
    dateTo?: boolean;
    contentIds?: boolean;
    tagIds?: boolean;
    contentTypes?: boolean;
    countryCodes?: boolean;
    creatorIds?: boolean;
    userDeviceTypes?: boolean;
    userDeviceOss?: boolean;
    linkIds?: boolean;
    utmSources?: boolean;
    utmMediums?: boolean;
    utmCampaigns?: boolean;
    utmContents?: boolean;
    utmTerms?: boolean;
    emailAutomationIds?: boolean;
    productIds?: boolean;
    promocodeIds?: boolean;
    promocodeCampaignIds?: boolean;
    providers?: boolean;
    currency?: boolean;
    languages?: boolean;
  };
  /**
   * Убрать тип «эфир» из фильтра по типу контента: на развёртывании без
   * живых эфиров его нет ни в меню, ни в списках. Значение передаёт
   * вызывающая фича (она читает `instance.liveEnabled`), потому что
   * shared не импортирует entities (eslint boundaries).
   */
  hideStreamContentType?: boolean;
  /**
   * Провайдеры оплаты, которые предлагает селект «Провайдер»: набор
   * развёртывания (`instance.paymentProviders`), на ottkit.io Stripe и
   * Paynet, на adult-инстансе только CCBill. Значение передаёт вызывающая
   * фича через `useInstancePaymentProviders`, потому что shared не
   * импортирует entities (eslint boundaries).
   *
   * `null` это «ответ инстанса ещё не пришёл»: в списке остаётся только
   * «Любой», иначе на ottkit.io мелькнёт CCBill, а на adult-инстансе
   * Stripe. `undefined` (проп не передан) оставляет все провайдеры схемы:
   * селект без явного набора ведёт себя как раньше.
   */
  paymentProviders?: PaymentProviderType[] | null;
  action?: ReactNode;
}

// Провайдеры оплаты: значения — enum `PaymentProvider` из схемы, порядок
// общий с остальным интерфейсом (PAYMENT_PROVIDER_ORDER), подписи из общей
// карты `PAYMENT_PROVIDER_LABEL_KEY`, той же, что у колонок «Провайдер» в
// списках финансов. Набор сужается до провайдеров развёртывания: раньше
// селект знал ровно Stripe и Paynet, поэтому на adult-инстансе предлагал
// двух провайдеров, которых там нет, и не давал отфильтровать CCBill.
function buildProviderOptions(
  paymentProviders: PaymentProviderType[] | null | undefined
): { value: PaymentProviderType; labelKey: string }[] {
  const allowed =
    paymentProviders === undefined
      ? PAYMENT_PROVIDER_ORDER
      : PAYMENT_PROVIDER_ORDER.filter((provider) =>
          (paymentProviders ?? []).includes(provider)
        );

  return allowed.map((value) => ({
    value,
    labelKey: PAYMENT_PROVIDER_LABEL_KEY[value],
  }));
}

const RELATIVE_DATE_CHIPS: {
  label: string;
  value: number;
  unit: "day" | "days" | "month" | "months";
}[] = [
  { label: "1day", value: 0, unit: "day" },
  { label: "1week", value: 6, unit: "days" },
  { label: "1month", value: 1, unit: "month" },
  { label: "3months", value: 3, unit: "months" },
  { label: "6months", value: 6, unit: "months" },
  { label: "12months", value: 12, unit: "months" },
];

function getChipDates(chip: (typeof RELATIVE_DATE_CHIPS)[number]) {
  return {
    dateFrom: moment().subtract(chip.value, chip.unit).startOf("day").format(),
    dateTo: moment().endOf("day").format(),
  };
}

/**
 * Значение поля задано пользователем. Отдельная проверка вместо `filter[key]`
 * нужна из-за булевых фильтров: `false` (автопродление выключено) — заданное
 * значение, а не пустое, и поле с ним обязано оставаться видимым.
 */
function hasFilterValue(value: unknown): boolean {
  if (value === undefined || value === null || value === "") return false;
  if (Array.isArray(value)) return value.length > 0;
  return true;
}

export const Filter: FC<FilterProps> = ({
  title,
  alwaysVisible = [],
  subheader,
  dateChips,
  quickFilters,
  selects = [],
  fields = {},
  hideStreamContentType = false,
  paymentProviders,
  action,
}) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile("md");
  const { filter, onChangeFilter, onClearFilter, onChangeParams } = useFilter();
  const providerOptions = useMemo(
    () => buildProviderOptions(paymentProviders),
    [paymentProviders]
  );

  const [opened, setOpened] = useState(false);

  const onClickMoreFilters = () => {
    setOpened((v) => !v);
  };

  // Общее правило видимости и для `fields`, и для `selects`: поле показано,
  // когда фильтр раскрыт, поле помечено alwaysVisible или у него есть значение.
  const isFieldVisible = (field: string): boolean =>
    hasFilterValue(filter[field]) || alwaysVisible.includes(field) || opened;

  const isVisible = (field: string): boolean => {
    if (!fields[field as keyof typeof fields]) {
      return false;
    }

    return isFieldVisible(field);
  };

  const anyVisible =
    opened || alwaysVisible.length > 0 || Object.keys(filter).length > 0;

  const fieldsCount = Object.keys(fields).length + selects.length;

  const showClearButton = !!Object.keys(filter).length && !isMobile;
  const showFilterButton = fieldsCount > alwaysVisible.length;

  const md = fieldsCount > 1 ? 6 : 12;

  return (
    <Paper elevation={0} sx={{ p: 2 }}>
      <Stack spacing={isMobile ? 1 : 2}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Typography variant="h5">{title}</Typography>
            <Box style={{ marginLeft: "auto" }}>
              <Stack spacing={1} direction="row">
                {showClearButton && (
                  <Tooltip title={t("shared.filter.clearTooltip")}>
                    <IconButton
                      size={isMobile ? "small" : "medium"}
                      onClick={onClearFilter}
                      sx={{
                        marginTop: "-8px",
                        marginBottom: "-8px",
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Tooltip>
                )}

                {showFilterButton && (
                  <>
                    {!opened ? (
                      <Tooltip title={t("shared.filter.openTooltip")}>
                        <IconButton
                          size={isMobile ? "small" : "medium"}
                          onClick={onClickMoreFilters}
                          sx={{
                            marginTop: "-8px",
                            marginBottom: "-8px",
                          }}
                        >
                          <FilterListIcon />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <Tooltip title={t("shared.filter.closeTooltip")}>
                        <IconButton
                          size={isMobile ? "small" : "medium"}
                          onClick={onClickMoreFilters}
                          sx={{
                            marginTop: "-8px",
                            marginBottom: "-8px",
                          }}
                        >
                          <CloseIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </>
                )}

                {action}
              </Stack>
            </Box>
          </Stack>
        </Box>
        {subheader}

        {(quickFilters ?? []).map((quickFilter) => (
          <Stack
            key={quickFilter.name}
            direction="row"
            spacing={1}
            useFlexGap
            flexWrap="wrap"
            alignItems="center"
          >
            {quickFilter.label && (
              <Typography variant="body2" color="text.secondary">
                {quickFilter.label}
              </Typography>
            )}

            {quickFilter.options.map((option) => {
              const selected = filter[quickFilter.name] === option.value;

              return (
                <Chip
                  key={option.value}
                  label={option.label}
                  clickable
                  color={selected ? "primary" : "default"}
                  onClick={() =>
                    onChangeFilter(
                      quickFilter.name,
                      selected ? undefined : option.value
                    )
                  }
                />
              );
            })}
          </Stack>
        ))}

        {dateChips === "RELATIVE" && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            {RELATIVE_DATE_CHIPS.map((chip) => {
              const { dateFrom, dateTo } = getChipDates(chip);
              const selected =
                filter.dateFrom &&
                filter.dateTo &&
                moment(filter.dateFrom).format() === dateFrom &&
                moment(filter.dateTo).format() === dateTo;

              return (
                <Chip
                  key={chip.label}
                  label={t(`shared.dateChips.${chip.label}`)}
                  clickable
                  color={selected ? "primary" : "default"}
                  onClick={() =>
                    onChangeParams({
                      "filter[dateFrom]": dateFrom,
                      "filter[dateTo]": dateTo,
                    })
                  }
                />
              );
            })}
          </Stack>
        )}

        {anyVisible && (
          <Box>
            <Grid container spacing={2} alignItems="center">
              {isVisible("search") && (
                <Grid
                  size={{
                    xs: 12,
                    md: md,
                  }}
                >
                  <TextField
                    fullWidth
                    variant="outlined"
                    placeholder={t("shared.filter.searchPlaceholder")}
                    value={filter.search || ""}
                    onChange={(e) => onChangeFilter("search", e.target.value)}
                    InputProps={{
                      endAdornment: filter.search && (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => onChangeFilter("search", "")}
                            size="large"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              )}
              {selects.map(
                (select) =>
                  isFieldVisible(select.name) && (
                    <Grid key={select.name} size={{ xs: 12, md }}>
                      <TextField
                        select
                        fullWidth
                        label={select.label}
                        // Значение всегда строка: в querystring булев фильтр
                        // (автопродление) живёт как "true"/"false", а
                        // FilterProvider отдаёт его уже булевым.
                        value={String(
                          filter[select.name] ?? select.defaultValue ?? ""
                        )}
                        onChange={(event) =>
                          onChangeFilter(select.name, event.target.value)
                        }
                      >
                        {!select.hideAnyOption && (
                          <MenuItem value="">{t("shared.filter.any")}</MenuItem>
                        )}
                        {select.options.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  )
              )}
              {isVisible("dateFrom") && (
                <Grid size={{ xs: 12, md }}>
                  <DateTimePicker
                    label={t("shared.filter.dateFrom")}
                    value={filter.dateFrom ? moment(filter.dateFrom) : null}
                    onChange={(value) =>
                      onChangeFilter("dateFrom", value?.toISOString() ?? "")
                    }
                    maxDate={filter.dateTo ? moment(filter.dateTo) : undefined}
                    sx={{ width: "100%" }}
                  />
                </Grid>
              )}
              {isVisible("dateTo") && (
                <Grid size={{ xs: 12, md }}>
                  <DateTimePicker
                    label={t("shared.filter.dateTo")}
                    value={filter.dateTo ? moment(filter.dateTo) : null}
                    onChange={(value) =>
                      onChangeFilter("dateTo", value?.toISOString() ?? "")
                    }
                    minDate={
                      filter.dateFrom ? moment(filter.dateFrom) : undefined
                    }
                    sx={{ width: "100%" }}
                  />
                </Grid>
              )}
              {isVisible("contentIds") && (
                <Grid size={{ xs: 12, md }}>
                  <ContentsAutocomplete
                    label={t("shared.filter.contents")}
                    value={filter.contentIds || []}
                    onChange={(contentIds) =>
                      onChangeFilter("contentIds", contentIds)
                    }
                  />
                </Grid>
              )}
              {isVisible("tagIds") && (
                <Grid size={{ xs: 12, md }}>
                  <TagsAutocomplete
                    label={t("shared.filter.tags")}
                    value={filter.tagIds || []}
                    onChange={(tagIds) => onChangeFilter("tagIds", tagIds)}
                  />
                </Grid>
              )}
              {isVisible("contentTypes") && (
                <Grid size={{ xs: 12, md }}>
                  <ContentTypeField
                    label={t("shared.filter.contentType")}
                    fullWidth
                    hideStream={hideStreamContentType}
                    value={filter.contentTypes || []}
                    onChange={(contentTypes) =>
                      onChangeFilter("contentTypes", contentTypes)
                    }
                  />
                </Grid>
              )}
              {isVisible("countryCodes") && (
                <Grid size={{ xs: 12, md }}>
                  <CountryCodeField
                    label={t("shared.filter.country")}
                    fullWidth
                    value={filter.countryCodes || []}
                    onChange={(countryCodes) =>
                      onChangeFilter("countryCodes", countryCodes)
                    }
                  />
                </Grid>
              )}
              {isVisible("creatorIds") && (
                <Grid size={{ xs: 12, md }}>
                  <CreatorsAutocomplete
                    label={t("shared.filter.creators")}
                    value={filter.creatorIds || []}
                    onChange={(creatorIds) =>
                      onChangeFilter("creatorIds", creatorIds)
                    }
                  />
                </Grid>
              )}
              {isVisible("userDeviceTypes") && (
                <Grid size={{ xs: 12, md }}>
                  <DeviceTypeField
                    label={t("shared.filter.deviceType")}
                    fullWidth
                    value={filter.userDeviceTypes || []}
                    onChange={(userDeviceTypes) =>
                      onChangeFilter("userDeviceTypes", userDeviceTypes)
                    }
                  />
                </Grid>
              )}
              {isVisible("userDeviceOss") && (
                <Grid size={{ xs: 12, md }}>
                  <DeviceOsField
                    label={t("shared.filter.deviceOs")}
                    fullWidth
                    value={filter.userDeviceOss || []}
                    onChange={(userDeviceOss) =>
                      onChangeFilter("userDeviceOss", userDeviceOss)
                    }
                  />
                </Grid>
              )}
              {isVisible("productIds") && (
                <Grid size={{ xs: 12, md }}>
                  <ProductsAutocomplete
                    label={t("shared.filter.products")}
                    value={filter.productIds || []}
                    onChange={(productIds) =>
                      onChangeFilter("productIds", productIds)
                    }
                  />
                </Grid>
              )}
              {isVisible("promocodeIds") && (
                <Grid size={{ xs: 12, md }}>
                  <PromocodesAutocomplete
                    label={t("shared.filter.promocodes")}
                    value={filter.promocodeIds || []}
                    onChange={(promocodeIds) =>
                      onChangeFilter("promocodeIds", promocodeIds)
                    }
                  />
                </Grid>
              )}
              {isVisible("promocodeCampaignIds") && (
                <Grid size={{ xs: 12, md }}>
                  <PromocodeCampaignsAutocomplete
                    label={t("shared.filter.promocodeCampaigns")}
                    value={filter.promocodeCampaignIds || []}
                    onChange={(ids) =>
                      onChangeFilter("promocodeCampaignIds", ids)
                    }
                  />
                </Grid>
              )}
              {isVisible("providers") && (
                <Grid size={{ xs: 12, md }}>
                  <TextField
                    select
                    fullWidth
                    label={t("shared.filter.provider")}
                    value={filter.providers?.[0] || ""}
                    onChange={(e) =>
                      onChangeFilter(
                        "providers",
                        e.target.value ? [e.target.value] : []
                      )
                    }
                  >
                    <MenuItem value="">{t("shared.filter.any")}</MenuItem>
                    {providerOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {t(option.labelKey)}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
              )}
              {isVisible("currency") && (
                <Grid size={{ xs: 12, md }}>
                  <CurrencyField
                    label={t("shared.filter.currency")}
                    fullWidth
                    value={filter.currency || ""}
                    onChange={(currency) =>
                      onChangeFilter("currency", currency)
                    }
                  />
                </Grid>
              )}
              {isVisible("languages") && (
                <Grid size={{ xs: 12, md }}>
                  <LanguageField
                    label={t("shared.filter.language")}
                    fullWidth
                    value={filter.languages || []}
                    onChange={(languages) =>
                      onChangeFilter("languages", languages)
                    }
                  />
                </Grid>
              )}
              {isVisible("linkIds") && (
                <Grid size={{ xs: 12, md }}>
                  <LinksAutocomplete
                    label={t("shared.filter.links")}
                    value={filter.linkIds || []}
                    onChange={(linkIds) => onChangeFilter("linkIds", linkIds)}
                  />
                </Grid>
              )}
              {isVisible("utmSources") && (
                <Grid size={{ xs: 12, md }}>
                  <UtmField
                    dimension="SOURCE"
                    label={t("shared.filter.utmSource")}
                    value={filter.utmSources || []}
                    onChange={(utmSources) =>
                      onChangeFilter("utmSources", utmSources)
                    }
                  />
                </Grid>
              )}
              {isVisible("utmMediums") && (
                <Grid size={{ xs: 12, md }}>
                  <UtmField
                    dimension="MEDIUM"
                    label={t("shared.filter.utmMedium")}
                    value={filter.utmMediums || []}
                    onChange={(utmMediums) =>
                      onChangeFilter("utmMediums", utmMediums)
                    }
                  />
                </Grid>
              )}
              {isVisible("utmCampaigns") && (
                <Grid size={{ xs: 12, md }}>
                  <UtmField
                    dimension="CAMPAIGN"
                    label={t("shared.filter.utmCampaign")}
                    value={filter.utmCampaigns || []}
                    onChange={(utmCampaigns) =>
                      onChangeFilter("utmCampaigns", utmCampaigns)
                    }
                  />
                </Grid>
              )}
              {isVisible("utmContents") && (
                <Grid size={{ xs: 12, md }}>
                  <UtmField
                    dimension="CONTENT"
                    label={t("shared.filter.utmContent")}
                    value={filter.utmContents || []}
                    onChange={(utmContents) =>
                      onChangeFilter("utmContents", utmContents)
                    }
                  />
                </Grid>
              )}
              {isVisible("utmTerms") && (
                <Grid size={{ xs: 12, md }}>
                  <UtmField
                    dimension="TERM"
                    label={t("shared.filter.utmTerm")}
                    value={filter.utmTerms || []}
                    onChange={(utmTerms) =>
                      onChangeFilter("utmTerms", utmTerms)
                    }
                  />
                </Grid>
              )}
              {isVisible("emailAutomationIds") && (
                <Grid size={{ xs: 12, md }}>
                  <EmailCampaignsAutocomplete
                    label={t("shared.filter.emailCampaigns")}
                    value={filter.emailAutomationIds || []}
                    onChange={(ids) =>
                      onChangeFilter("emailAutomationIds", ids)
                    }
                  />
                </Grid>
              )}
            </Grid>
          </Box>
        )}
      </Stack>
    </Paper>
  );
};
